import { Popover, Transition } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import {
  type ComponentPropsWithoutRef,
  Fragment,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type PaginatedResults,
  type PaginationRequest,
} from "shared/api/types/pagination";
import ChevronDownIcon from "shared/components/icons/chevron/ChevronDownIcon";
import CrossIcon from "shared/components/icons/sign/CrossIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { usePillarTableFilterState } from "shared/components/pillar-table/filters/hooks/usePillarTableFilterState";
import PillarTableFilterLabel from "shared/components/pillar-table/filters/PillarTableFilterLabel";
import { usePillarTableFilterOptionSources } from "shared/components/pillar-table/filters/PillarTableFilterOptionSources";
import PillarTableFilterSelectPageButton from "shared/components/pillar-table/filters/PillarTableFilterSelectPageButton";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import {
  type FilterChoice,
  type FiltersRequest,
} from "shared/filter-where-clause";

export interface SelectOptionQueryParams extends PaginationRequest {
  requestFilters: FiltersRequest;
}

// Union of flat/paginated lists as a result of a query
export type SelectOptionQueryResult =
  | FilterChoice[]
  | PaginatedResults<FilterChoice>;

export type SelectOptionQuery = (
  params: SelectOptionQueryParams,
) => Promise<SelectOptionQueryResult>;

type InitialValueType<M extends boolean> = M extends true
  ? string[]
  : string | undefined;

type FilterValueType<M extends boolean> = M extends true
  ? string[]
  : string | undefined;

export type PillarTableFilterSelectProps<M extends boolean> =
  ComponentPropsWithoutRef<typeof Popover> & {
    isMultiple?: M;
    defaultValue?: InitialValueType<M>;
    title: string;
    toolTip?: ReactNode;
    filterKey: string;
    choices?: FilterChoice[];
    className?: string;
    popupContainerClassName?: string;
    queryKey?: string;
    selectOptionQuery?: SelectOptionQuery;
    pagination?: boolean;
    pageSize?: number;
    /**
     * The options depend on the table's other filters, which
     * `selectOptionQuery` receives as `requestFilters`. They load when the
     * menu opens, and again on a later open once another filter has changed.
     * This menu's own selection doesn't reload them.
     */
    narrowByTableFilters?: boolean;
  };

type PanelOpenStateProps = {
  /** Called with true when the panel mounts and false when it unmounts */
  onOpenChange: (isOpen: boolean) => void;
};

/** Reports whether the popover is open; Headless UI mounts its panel only while open */
const PanelOpenState = ({ onOpenChange }: PanelOpenStateProps) => {
  useEffect(() => {
    onOpenChange(true);
    return () => onOpenChange(false);
  }, [onOpenChange]);
  return null;
};

const PillarTableFilterSelectMenuPopover = <M extends boolean>({
  isMultiple,
  defaultValue,
  filterKey,
  title,
  toolTip,
  choices,
  queryKey,
  className = "",
  popupContainerClassName = "",
  selectOptionQuery,
  pagination = false,
  pageSize = 500,
  narrowByTableFilters = false,
  ...props
}: PillarTableFilterSelectProps<M>) => {
  const filterContext = usePillarTableContext();
  const [isOpen, setIsOpen] = useState(false);
  // The table's other filters that hold a value, in a stable order; the
  // table keeps empty keys for unset filters and drops them once one is set
  const otherTableFiltersKey = narrowByTableFilters
    ? JSON.stringify(
        Object.entries(filterContext.filtersRequest)
          .filter(
            ([key, value]) =>
              key !== filterKey &&
              value !== undefined &&
              value !== null &&
              value !== "" &&
              !(Array.isArray(value) && value.length === 0),
          )
          .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey)),
      )
    : undefined;
  const { setFilterValue, filterValue: filterValueFromRequest } =
    usePillarTableFilterState<string | string[] | undefined>({
      filterKey,
      defaultValue,
    });

  const filterValue = (
    isMultiple ? (filterValueFromRequest ?? []) : (filterValueFromRequest ?? "")
  ) as FilterValueType<M>;

  const updateFilter = (newValue: FilterValueType<M>) => {
    setFilterValue(newValue);
  };

  const [paginationState, setPaginationState] = useState<
    PaginationRequest | undefined
  >(pagination ? { page: 1, pageSize } : undefined);

  // Reset to page 1 whenever queryKey changes
  useEffect(() => {
    setPaginationState((prevState) =>
      !prevState || prevState.page === 1
        ? prevState
        : { ...prevState, page: 1 },
    );
  }, [queryKey, filterKey, otherTableFiltersKey]);

  const queryResult = useQuery<SelectOptionQueryResult>({
    queryKey: [
      "selectOptions",
      // Each table keeps its own options: a report page's Category menu and
      // the profiles table's load different lists under the same filterKey
      filterContext.testidTable,
      filterKey,
      queryKey,
      // A custom report's menu with stored choices mustn't share the table's
      // own menu's options, which load under the same filterKey
      choices,
      filterContext.dynamicFilterChoices,
      paginationState,
      ...(narrowByTableFilters ? [otherTableFiltersKey] : []),
    ],
    enabled: !narrowByTableFilters || isOpen,
    // Narrowed options are kept per filter combination, so reopening the menu
    // without another filter changing doesn't load them again
    staleTime: narrowByTableFilters ? Infinity : undefined,
    queryFn: async () => {
      if (selectOptionQuery) {
        return selectOptionQuery({
          requestFilters: filterContext.filtersRequest,
          page: paginationState?.page ?? 1,
          pageSize: paginationState?.pageSize ?? pageSize,
        });
      }
      const dynamicFilterOption =
        filterContext?.dynamicFilterChoices?.[filterKey];
      return choices ?? dynamicFilterOption ?? [];
    },
    keepPreviousData: pagination,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const optionsResult = queryResult?.data ?? [];

  const options = Array.isArray(optionsResult)
    ? optionsResult
    : optionsResult.results;
  const totalResults = Array.isArray(optionsResult)
    ? optionsResult.length
    : optionsResult.totalResults;

  const currentPage = paginationState?.page ?? 1;
  const totalPageCount = Math.ceil(
    totalResults / (paginationState?.pageSize ?? pageSize),
  );
  const hasPreviousPage = !!paginationState && currentPage > 1;
  const hasNextPage = !!paginationState && currentPage < totalPageCount;
  const isLoadingOptions =
    narrowByTableFilters && queryResult.isFetching && options.length === 0;

  const { effectiveValue, selectedOptions } = useMemo(() => {
    const userSelection = filterValue != null ? filterValue : defaultValue;

    const currentIds = (() => {
      if (!userSelection) {
        return [];
      }

      const baseArray = Array.isArray(userSelection)
        ? userSelection
        : [userSelection];
      return baseArray.map(String);
    })();

    const selected = options.filter((option) =>
      currentIds.includes(option.value.toString()),
    );

    return {
      effectiveValue: currentIds,
      selectedOptions: selected,
    };
  }, [filterValue, defaultValue, options]);

  return (
    <Popover
      {...props}
      id={`filter-select-popover-${queryKey ?? filterKey}`}
      className={`relative rounded flex flex-col ${className}`}
    >
      <PillarTableFilterLabel
        className="text-neutral-mid-600"
        filterKey={queryKey ?? filterKey}
        title={title}
        toolTip={toolTip}
      />
      <Popover.Button
        id={`filter-select-popover-button-${filterKey}`}
        className="w-full p-1 h-4.75 border rounded border-neutral-mid-200 flex group relative flex items-center"
      >
        <div
          data-testid={`filter-select-popover-button-${filterKey}`}
          className="pillartable-filters-selectmenu-button-title mr-0.25 whitespace-nowrap"
        >
          {isMultiple ? title : selectedOptions?.[0]?.display || title}
        </div>

        {isMultiple &&
          Array.isArray(effectiveValue) &&
          effectiveValue.length > 0 && (
            <div
              data-testid={`filter-count-${filterKey}`}
              className="pillartable-filters-selectmenu-button-count mx-0.25 mt-0.25"
            >
              {effectiveValue.length}
            </div>
          )}

        <ChevronDownIcon
          className="pillartable-filters-selectmenu-button-icon h-2.5 w-2.5 ml-auto"
          aria-hidden="true"
        />
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        {/* z-30: above the sticky table header (z-20) and fixed columns (z-10),
            below modals (z-50). At z-10 the header painted over any option that
            landed in its band, so short option lists were partly unclickable. */}
        <Popover.Panel
          className={`pillartable-filters-selectmenu-popover-container min-w-[150px] max-h-[500px] !p-0 absolute top-7.5 z-30 origin-top-left border border-neutral-light-500 flex flex-col overflow-auto phone:fixed phone:inset-0 phone:max-h-full ${popupContainerClassName}`}
        >
          {({ close }) => (
            <>
              {narrowByTableFilters && (
                <PanelOpenState onOpenChange={setIsOpen} />
              )}
              <div className="hidden bg-neutral-light p-1 sticky top-0 grid grid-cols-[48px_1fr_48px] items-center gap-1 phone:block">
                <h3 className="appmodal-panel-title flex justify-center col-start-2">
                  {title}
                </h3>
                <PillarForm.Button
                  className="!absolute top-1/2 -translate-y-1/2 right-0 button-regular-general-unfilled size-6 rounded-full col-start-3"
                  onClick={() => close()}
                >
                  <CrossIcon className="size-3" />
                </PillarForm.Button>
              </div>

              {options.length === 0 && (
                <span className="grow p-1 flex justify-center items-center w-full whitespace-nowrap text-subtle">
                  {isLoadingOptions ? "Loading…" : "No filters"}
                </span>
              )}

              {options.length > 0 && (
                <div className="flex-1 p-1 flex flex-col">
                  {hasPreviousPage && (
                    <PillarTableFilterSelectPageButton
                      filterKey={filterKey}
                      direction="prev"
                      onClick={() =>
                        setPaginationState((prevState) => ({
                          ...prevState!,
                          page: prevState!.page - 1,
                        }))
                      }
                    />
                  )}
                  {options.map((item) => {
                    const value = item.value;
                    const valueStr = value.toString();
                    const commonLabelClass =
                      "p-1 w-full whitespace-nowrap cursor-pointer flex items-center phone:min-h-sm phone:min-w-sm";

                    if (isMultiple) {
                      const isChecked = effectiveValue.includes(valueStr);

                      return (
                        <div
                          key={`filterKey-${value}`}
                          className="flex items-center relative"
                        >
                          <input
                            checked={isChecked}
                            value={value}
                            onChange={(e) => {
                              const currentValue = (
                                Array.isArray(filterValue) ? filterValue : []
                              ).map((item) => item.toString());
                              const tempFilterValue = currentValue.filter(
                                (item) => item !== value.toString(),
                              );
                              if (e.target.checked) {
                                tempFilterValue.push(value.toString());
                              }
                              updateFilter(
                                tempFilterValue as FilterValueType<M>,
                              );
                            }}
                            id={`filter-checkbox-${filterKey}-${value}`}
                            data-testid={`filter-checkbox-${filterKey}-${value}`}
                            name={`filter-checkbox-${filterKey}-${value}`}
                            type="checkbox"
                            className="absolute top-1/2 left-0 -translate-y-1/2"
                          />
                          <label
                            htmlFor={`filter-checkbox-${filterKey}-${value}`}
                            data-testid={`filter-label-${filterKey}-${value}`}
                            className={`pl-3 pr-1 ${commonLabelClass} `}
                          >
                            {item.display}
                          </label>
                        </div>
                      );
                    }

                    const isSelected = filterValue === valueStr;
                    return (
                      <button
                        key={`filterKey-${value}`}
                        className="cursor-pointer"
                        id={`filter-button-${filterKey}-${value}`}
                        data-testid={`filter-button-${filterKey}-${value}`}
                        type="button"
                        onClick={() => {
                          updateFilter(value.toString() as FilterValueType<M>);
                          close();
                        }}
                      >
                        <label
                          data-testid={`filter-label-${filterKey}-${value}`}
                          className={`hover:bg-neutral-light-300 ${isSelected ? "bg-neutral-light-400 font-semibold" : ""} ${commonLabelClass}`}
                        >
                          {item.display}
                        </label>
                      </button>
                    );
                  })}
                  {hasNextPage && (
                    <PillarTableFilterSelectPageButton
                      filterKey={filterKey}
                      direction="next"
                      onClick={() =>
                        setPaginationState((prevState) => ({
                          ...prevState!,
                          page: prevState!.page + 1,
                        }))
                      }
                    />
                  )}
                </div>
              )}

              {options.length > 0 && (
                <div className="bg-neutral-light p-1 sticky bottom-0 flex justify-center gap-1">
                  <PillarForm.Button
                    className="w-full phone:min-h-sm phone:min-w-sm"
                    onClick={() => {
                      const newValue = isMultiple ? [] : "";
                      updateFilter(newValue as FilterValueType<M>);
                      close();
                    }}
                  >
                    Clear
                  </PillarForm.Button>
                </div>
              )}
            </>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};

/**
 * While a custom report's filters replace the table's, the table's own menu
 * renders nothing and hands over how it loads its options, so the report's
 * menu of the same filterKey can load them the same way.
 */
const PillarTableFilterSelectMenu = <M extends boolean>(
  props: PillarTableFilterSelectProps<M>,
) => {
  const optionSources = usePillarTableFilterOptionSources();

  if (optionSources?.isCollecting) {
    const {
      filterKey,
      choices,
      selectOptionQuery,
      queryKey,
      narrowByTableFilters,
      pagination,
      pageSize,
    } = props;
    // Only a menu with options of its own; one that reads the list
    // response's dynamicFilterChoices has nothing to hand over. Set while
    // rendering rather than in an effect: the report's menus render next in
    // this same pass and read it.
    if (selectOptionQuery || choices?.length) {
      optionSources.sources.set(filterKey, {
        choices,
        selectOptionQuery,
        queryKey,
        narrowByTableFilters,
        pagination,
        pageSize,
      });
    }
    return null;
  }

  return <PillarTableFilterSelectMenuPopover {...props} />;
};

export default PillarTableFilterSelectMenu;
